// @flow

import DataObjectParser from 'dataobject-parser';
import fromPairs from 'lodash/fromPairs';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import startsWith from 'lodash/startsWith';
import _values from 'lodash/values'; // named _values to avoid shadowing variables

import { createSelector } from 'reselect';
import type { OutputSelector } from 'reselect';

import {
  selectProductErrors,
  selectOneProduct,
  selectProductsByType,
  selectRawProductsArray,
  type Product,
  type ProductContactErrors,
  type DecoratedProduct,
  type ProductId,
} from '@gandi/cart';
import { selectTldInfo, type Process } from '@gandi/domain-configuration';

import type { FullState } from '../../../../types';
import type {
  FormValues,
  ProductDomainInfo,
  State,
  Step,
  PurchaseGroupId,
  Tld,
  UISteps,
  UIStepRole,
  ProductPayload,
  Role,
  InnerSteps,
  GlobalErrors,
  TrusteePrices,
} from '../types';

const EMPTY_OBJECT = {};

/**
 * Select the Contact Wizard state
 */
export const selectRawState = (state: FullState): State =>
  state.configure.domainContactsWizard.wizard;

// FIXME: To remove
// used as selector to pass state to the selector function
const passState = (state: FullState) => state;

// used as selector to pass props to the selector function
const passProps = (state: FullState, props: any) => props;

/**
 * Select the deleting flag
 */
export const selectDeletingStatus = (state: FullState): boolean =>
  state.configure.domainContactsWizard.wizard.isDeleting;

/**
 * Select the working flag
 */
export const selectWorkingStatus = (state: FullState): boolean =>
  state.configure.domainContactsWizard.wizard.isWorking;

/**
 * Select the validating flag
 */
export const selectValidatingStatus = (state: FullState): boolean =>
  state.configure.domainContactsWizard.wizard.isValidating;

/**
 * Select innerSteps state
 */
export const selectRawInnerSteps: (state: FullState) => InnerSteps =
  createSelector([selectRawState], rawState => rawState.innerSteps);

/**
 * Select raw steps from the state
 */
export const selectRawSteps: (state: FullState) => Array<Step> = createSelector(
  [selectRawState],
  rawState => rawState.steps,
);

/**
 * Select the raw current step index (of the validation loop)
 */
export const selectCurrentStepIndex: (state: FullState) => number =
  createSelector([selectRawState], rawState => rawState.currentStepIndex);

/**
 * Select the current step
 */
export const selectCurrentStep: (state: FullState) => ?Step | void =
  createSelector(
    [selectRawSteps, selectCurrentStepIndex],
    (allSteps: Array<Step>, currentStepIndex: number) =>
      currentStepIndex >= 0 && currentStepIndex < allSteps.length
        ? allSteps[currentStepIndex]
        : null,
  );

/**
 * Select global errors
 */
export const selectGlobalErrors: (state: FullState) => GlobalErrors =
  createSelector([selectRawState], rawState => rawState.globalErrors);

/**
 * Select the flag that indicates the wizard is ready (steps prepared and global errors fetched)
 */
export const selectIsWizardReady = (state: FullState): boolean =>
  state.configure.domainContactsWizard.wizard.isWizardReady;

/**
 * Creates a selector which returns the products of a given productType and of process belonging to a given process list.
 */
export const selectProductsByTypeAndProcessesFactory = (conf: {
  productType: string,
  processes: Array<Process>,
}): OutputSelector<FullState, mixed, Array<DecoratedProduct>> =>
  createSelector(
    // $FlowFixMe[prop-missing]
    [(state: FullState) => selectProductsByType(state, conf)],
    (products: Array<DecoratedProduct>) =>
      products.filter(product => conf.processes.includes(product.process)),
  );

export const computePurchaseGroupId = (
  product: DecoratedProduct,
): ProductId | string => {
  if (product.process === 'change_owner') {
    // Note: `payload.purchaseGroupId` is currently only set via a Bulk Owner Change.
    // $FlowFixMe refine @gandi/cart ProductPayload
    return product.payload.purchaseGroupId || product.id;
  }

  /* Wizard handles create, transfer, change_owner
   for each process the owner can be different.
   A cart can contain products from each process
   each with different owners hence we need to call global-check for each
   purchaseGroup by process.
| domain                           | feature                  | owner   |
|----------------------------------|--------------------------|---------|-
| create.pt                        | account-choice           | owner A |
| transfer.pt                      | cart-overview            | owner B |
| change-owner.pt                  | domain-change-owner      | owner C |
| bulk-change-owner.pt             | domain-bulk-change-owner | owner D |
| bulk-change-owner-other-owner.pt | domain-bulk-change-owner | owner E |
  */
  return `noPurchaseGroupId-${product.process}`;
};

function isHostingWithDomain(product: DecoratedProduct): boolean {
  const { productType, payload } = product;
  return productType === 'paas' && payload.domain?.fqdn !== undefined;
}

/**
 * Return the list of cart products that contains contact errors
 * (when error.name starts by a role)
 */
export const selectProductsWithDomainContactError: (
  state: FullState,
) => Array<ProductDomainInfo> = createSelector(
  [passState, selectProductErrors],
  (state: FullState, productErrors: ProductContactErrors) => {
    const roles = ['owner', 'admin', 'tech', 'bill'];
    return productErrors
      .filter(
        productError =>
          productError.product.productType === 'domain' ||
          isHostingWithDomain(productError.product),
      )
      .filter(productError => {
        const errorsKeys = Object.keys(productError.errors);
        return errorsKeys.some(key =>
          roles.some(role => startsWith(key, role)),
        );
      })
      .map(productError => {
        const product = productError.product;

        const id: ProductId = product.id;
        const fqdn =
          // case of domain create / renew / transfer
          product.payload.fqdn ||
          // case of domain attached to hosting product
          product.payload.domain?.fqdn ||
          // case of a change owner
          product?.identifier;
        // $FlowFixMe[incompatible-call]
        const tldInfo = selectTldInfo(state, { fqdn });
        const tld =
          product.payload.tld ||
          product.payload.domain?.tld ||
          tldInfo.name ||
          (product.payload.fqdn || 'not-available') // this is last try to find a TLD from the FQDN
            .split('.')
            .slice(1)
            .join('.');
        const phase = product.payload.tld_phase || 'golive';
        const process = product.process || '';
        const contactRoles = roles.reduce<{ [Role]: FormValues, ... }>(
          (acc, role) => ({
            ...acc,
            // $FlowFixMe[invalid-computed-prop]
            [role]:
              product.payload[role] || // domain
              product.payload.domain?.[role] || // hosting domain
              EMPTY_OBJECT,
          }),
          {},
        );
        const purchaseGroupId = computePurchaseGroupId(product);

        // $FlowFixMe[cannot-spread-indexer]
        return {
          id,
          tld,
          fqdn,
          phase,
          process,
          ...contactRoles,
          purchaseGroupId,
        };
      });
  },
);

/**
 * Select the products to patch/delete
 */

export const selectProductsToPatchOrRemove: OutputSelector<
  FullState,
  {
    purchaseGroupId: PurchaseGroupId,
    tld: Tld,
    process: Process,
  },
  Array<Product>,
> = createSelector(
  [passState, passProps, selectProductsWithDomainContactError],
  (
    state: FullState,
    props: {
      purchaseGroupId: PurchaseGroupId,
      tld: Tld,
      process: Process,
    },
    productsWithDomainContactError: Array<ProductDomainInfo>,
  ) =>
    productsWithDomainContactError
      .filter(
        product =>
          product.purchaseGroupId === props.purchaseGroupId &&
          product.tld === props.tld &&
          product.process === props.process,
      )
      .map(product => {
        const fqdn = product.fqdn;
        return (
          selectOneProduct(state, { fqdn }) ||
          selectRawProductsArray(state).find(
            p => p.productType === 'paas' && p.payload.domain?.fqdn === fqdn,
          )
        );
      })
      .filter(Boolean),
);

/**
 * Select if we should patch contacts on the next visit to account-choice page.
 */
export const selectShouldPatchContacts: (state: FullState) => boolean =
  createSelector([selectRawState], rawState => rawState.shouldPatchContacts);

/**
 * Select the nested errors from the current step.
 */
// Use to bind errors to the form fields.
export const selectCurrentStepErrors: (state: FullState) => { ... } =
  createSelector([selectCurrentStep], (currentStep: ?Step) => {
    if (!currentStep) {
      return {};
    }

    // transform dot notations to nested objects
    // to map errors to the form
    const nested = DataObjectParser.transpose(currentStep.errors);
    return nested.data();
  });

type SelectFormattedPayloadProps = {
  purchaseGroupId: PurchaseGroupId,
  tld: Tld,
};
/**
 * Return a payload object used to patch the product into the cart.
 */
export const selectFormattedPayload: (
  state: FullState,
  SelectFormattedPayloadProps,
  // $FlowIgnore[incompatible-call]
) => ?ProductPayload = createSelector(
  [selectRawSteps, passProps],
  (allSteps: Array<Step>, props: SelectFormattedPayloadProps) => {
    const stepsForPurchaseGroupTld = allSteps.filter(
      step =>
        step.purchaseGroupId === props.purchaseGroupId &&
        step.domainInfo.tld === props.tld,
    );

    const roleToValues = fromPairs(
      stepsForPurchaseGroupTld.map(step => [step.role, step.values]),
    );

    // if the data is missing for some roles, returns null to avoid to patch the product if data are not complete.
    if (
      ['owner', 'admin', 'tech', 'bill'].some(role =>
        isEmpty(roleToValues[role]),
      )
    ) {
      return null;
    }

    // remove roles for which data are equal to the owner role
    // (removed contacts will be "as same owner")
    // It's not perfect:
    //
    // Example: The .pt requires a param for the admin, which leads to
    // duplicate all contacts since tech and bill inherits from that params
    // (prefilled in the next steps)
    const roleToValuesWithoutSameValuesAsOwner = fromPairs(
      Object.entries(roleToValues).filter(
        ([role, values]) =>
          role === 'owner' || !isEqual(values, roleToValues.owner),
      ),
    );

    return {
      ...roleToValuesWithoutSameValuesAsOwner,
      trustee_roles: stepsForPurchaseGroupTld
        .filter(step => step.trustee)
        .map(step => step.role),
    };
  },
);

/**
 * Return a struct convenient for the UI to display Rows into Collapsible components.
 *
 * [{
 *   domain: 'toto.com',
 *   roles: [{
 *     label: 'owner',
 *     status: 'success',
 *   }],
 * }]
 */
export const selectUiSteps: (state: FullState) => UISteps = createSelector(
  [selectRawSteps],
  (rawSteps: Array<Step>) =>
    _values(
      groupBy(
        rawSteps.filter(step => step.status !== 'pending'),
        step => step.domainInfo.fqdn,
      ),
    ).map((stepsByFqdn: Array<Step>) => {
      const roles: Array<UIStepRole> = stepsByFqdn.map(step => ({
        label: step.role,
        status: step.status,
        values: step.values,
      }));

      return {
        purchaseGroupId: stepsByFqdn[0].purchaseGroupId,
        domainInfo: stepsByFqdn[0].domainInfo,
        roles,
        collapsed: roles.every(role => role.status === 'success'),
      };
    }),
);

/**
 * Return role for which we need to put a trustee
 */
export const selectTrusteeRoleFlag = (state: FullState): ?Role =>
  state.configure.domainContactsWizard.wizard.trusteeRoleFlag;

/**
 * Return the trustee prices
 */
export const selectTrusteePrices = (state: FullState): ?TrusteePrices =>
  state.configure.domainContactsWizard.wizard.trusteePrices;

/**
 * Return a flag that indicates whether the wizard has some domains for tlds
 * offering a trustee option
 */
export const selectWizardHasTrusteeTlds: (state: FullState) => boolean =
  createSelector([selectGlobalErrors], (globalErrors: GlobalErrors) =>
    _values(globalErrors)
      .map(groupErrors =>
        _values(groupErrors)
          .map(tldErrors => tldErrors.trustee_roles?.length > 0)
          .some(Boolean),
      )
      .some(Boolean),
  );

/**
 * Return true if all steps have been succeeded.
 */
export const selectAllStepsAreSuccessful: (state: FullState) => boolean =
  createSelector([selectRawSteps], (rawSteps: Array<Step>) =>
    rawSteps.every(role => role.status === 'success'),
  );

// FIXME: memoization of these sub-selectors.
export const selectCurrentStepInitialValues: (state: FullState) => FormValues =
  createSelector(
    [
      selectProductsWithDomainContactError,
      selectCurrentStep,
      selectRawProductsArray,
    ],
    (
      products: Array<ProductDomainInfo>,
      step: ?Step,
      rawProducts: Array<Product>,
    ) => {
      if (!step) {
        return EMPTY_OBJECT;
      }

      const {
        purchaseGroupId,
        domainInfo: { phase, process, fqdn },
      } = step;

      const product = products.find(
        p =>
          p.purchaseGroupId === purchaseGroupId &&
          p.phase === phase &&
          p.process === process &&
          p.fqdn === fqdn,
      );

      if (!product) {
        return EMPTY_OBJECT;
      }

      const rawProduct = rawProducts.find(
        ({ payload }) =>
          payload.fqdn === product.fqdn ||
          payload.domain?.fqdn === product.fqdn,
      );

      return (
        (rawProduct &&
          (rawProduct.payload?.[step.role || 'owner'] ||
            rawProduct.payload?.owner ||
            rawProduct.payload?.domain?.[step.role || 'owner'] ||
            rawProduct.payload?.domain?.owner)) ||
        EMPTY_OBJECT
      );
    },
  );
